import React, { useEffect, useMemo, useCallback } from 'react';
import { useReportes } from './ReportesContex';
import Chart from 'react-apexcharts';
import { Clock, TrendingDown, Calendar } from 'tabler-icons-react';
import '../../styles/reportes.css';

const MULTI_COLORS = [
    '#6495ED', '#fe424d', '#2ccb9e', '#ffc800', '#E91E63',
    '#DB8E23', '#8A2B02', '#0B0AA0', '#0EA5E9', '#7BCCB5',
];

// ─── Color utilities ──────────────────────────────────────────────────────────

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function toArgb(hex) {
    return `FF${hex.toUpperCase()}`;
}

// ─── Excel export ─────────────────────────────────────────────────────────────

async function exportarExcel(tPerdidos, tPerdidosPvNP, detallePlane, detalleNoPlane, selectedArea, selectedLinea, selectedCelda) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const primary   = getPrimaryColor();
    const headerBg  = blendHex(primary, '000000', 0.8);
    const subHdrBg  = blendHex(primary, 'ffffff', 0.3);
    const infoBg    = blendHex(primary, 'ffffff', 0.85);

    const wb = new ExcelJS.Workbook();

    const styleHeader = (cell, text, bg) => {
        cell.value = text;
        cell.style = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(bg) } },
            alignment: { horizontal: 'center', vertical: 'middle' },
        };
    };

    const infoText = [
        selectedArea?.nombre  ? `Área: ${selectedArea.nombre}`   : '',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : '',
        selectedCelda?.nombre ? `Celda: ${selectedCelda.nombre}` : '',
    ].filter(Boolean).join('   |   ');

    // ── Sheet 1: Tiempos Perdidos ──────────────────────────────────────────────
    const ws1 = wb.addWorksheet('Tiempos Perdidos');
    if (infoText) {
        ws1.mergeCells('A1:B1');
        ws1.getCell('A1').value = infoText;
        ws1.getCell('A1').style = {
            font: { size: 11 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } },
        };
        ws1.getRow(1).height = 18;
    }
    styleHeader(ws1.getCell('A2'), 'Periodo', headerBg);
    styleHeader(ws1.getCell('B2'), 'Tiempo Perdido (min)', headerBg);
    ws1.getRow(2).height = 20;
    ws1.getColumn(1).width = 20;
    ws1.getColumn(2).width = 22;
    tPerdidos.forEach((t, i) => {
        const row = ws1.getRow(i + 3);
        row.getCell(1).value = t.periodo;
        row.getCell(2).value = t.valor;
        if (i % 2 !== 0) {
            row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
        }
        row.height = 16;
    });

    // ── Sheet 2: Planeados vs No Planeados ────────────────────────────────────
    const ws2 = wb.addWorksheet('PvNP');
    styleHeader(ws2.getCell('A1'), 'Periodo', headerBg);
    styleHeader(ws2.getCell('B1'), 'No Planeado (min)', headerBg);
    styleHeader(ws2.getCell('C1'), 'Planeado (min)', headerBg);
    ws2.getRow(1).height = 20;
    ws2.getColumn(1).width = 20;
    ws2.getColumn(2).width = 20;
    ws2.getColumn(3).width = 16;
    tPerdidosPvNP.forEach((t, i) => {
        const row = ws2.getRow(i + 2);
        row.getCell(1).value = t.periodo;
        row.getCell(2).value = t.valorNoPlaneados;
        row.getCell(3).value = t.valorPlaneados;
        if (i % 2 !== 0) {
            row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
        }
        row.height = 16;
    });

    // ── Sheet 3: Detalle Planeados ────────────────────────────────────────────
    if (detallePlane.length > 0) {
        const ws3 = wb.addWorksheet('Det. Planeados');
        const periodos = detallePlane[0].items.periodo ?? [];
        styleHeader(ws3.getCell('A1'), 'Motivo', headerBg);
        periodos.forEach((p, ci) => styleHeader(ws3.getCell(1, ci + 2), p, subHdrBg));
        ws3.getRow(1).height = 20;
        ws3.getColumn(1).width = 28;
        periodos.forEach((_, ci) => { ws3.getColumn(ci + 2).width = 14; });
        detallePlane.forEach((d, ri) => {
            const row = ws3.getRow(ri + 2);
            row.getCell(1).value = d.motivo;
            d.items.valor.forEach((v, ci) => { row.getCell(ci + 2).value = v; });
            row.height = 16;
        });
    }

    // ── Sheet 4: Detalle No Planeados ─────────────────────────────────────────
    if (detalleNoPlane.length > 0) {
        const ws4 = wb.addWorksheet('Det. No Planeados');
        const periodos = detalleNoPlane[0].items.periodo ?? [];
        styleHeader(ws4.getCell('A1'), 'Motivo', headerBg);
        periodos.forEach((p, ci) => styleHeader(ws4.getCell(1, ci + 2), p, subHdrBg));
        ws4.getRow(1).height = 20;
        ws4.getColumn(1).width = 28;
        periodos.forEach((_, ci) => { ws4.getColumn(ci + 2).width = 14; });
        detalleNoPlane.forEach((d, ri) => {
            const row = ws4.getRow(ri + 2);
            row.getCell(1).value = d.motivo;
            d.items.valor.forEach((v, ci) => { row.getCell(ci + 2).value = v; });
            row.height = 16;
        });
    }

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `tiempos_perdidos_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Tiempos() {
    const {
        tPerdidos, tPerdidosPvNP, detallePlane, detalleNoPlane,
        selectedLinea, selectedCelda, selectedArea,
        tPerdidosFetched, isLoadingLlamadas, setExportFn,
    } = useReportes();

    const handleExport = useCallback(
        () => exportarExcel(tPerdidos, tPerdidosPvNP, detallePlane, detalleNoPlane, selectedArea, selectedLinea, selectedCelda),
        [tPerdidos, tPerdidosPvNP, detallePlane, detalleNoPlane, selectedArea, selectedLinea, selectedCelda]
    );

    useEffect(() => {
        if (tPerdidos.length > 0) {
            setExportFn(() => handleExport);
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [tPerdidos.length, handleExport, setExportFn]);

    const nombreFiltro = selectedCelda?.nombre ?? selectedLinea?.nombre ?? '';

    // ─── KPIs ────────────────────────────────────────────────────────────────────
    const kpis = useMemo(() => {
        const totalP  = tPerdidosPvNP.reduce((a, b) => a + (b.valorPlaneados   ?? 0), 0);
        const totalNP = tPerdidosPvNP.reduce((a, b) => a + (b.valorNoPlaneados ?? 0), 0);
        const total   = totalP + totalNP;
        return {
            total,
            pctPlaneados:   total > 0 ? Math.round((totalP  / total) * 100) : 0,
            pctNoPlaneados: total > 0 ? Math.round((totalNP / total) * 100) : 0,
        };
    }, [tPerdidosPvNP]);

    // ─── Chart data ──────────────────────────────────────────────────────────────
    const chart1 = useMemo(() => ({
        series:     [{ data: tPerdidos.map((t) => t.valor) }],
        categories: tPerdidos.map((t) => t.periodo),
    }), [tPerdidos]);

    const chart2 = useMemo(() => ({
        series: [
            { name: 'No Planeado', data: tPerdidosPvNP.map((t) => t.valorNoPlaneados) },
            { name: 'Planeado',    data: tPerdidosPvNP.map((t) => t.valorPlaneados)   },
        ],
        categories: tPerdidosPvNP.map((t) => t.periodo),
    }), [tPerdidosPvNP]);

    const chart3 = useMemo(() => {
        if (!detallePlane.length) return { series: [], categories: [] };
        return {
            series:     detallePlane.map((d) => ({ name: d.motivo, data: d.items.valor })),
            categories: detallePlane[0].items.periodo,
        };
    }, [detallePlane]);

    const chart4 = useMemo(() => {
        if (!detalleNoPlane.length) return { series: [], categories: [] };
        return {
            series:     detalleNoPlane.map((d) => ({ name: d.motivo, data: d.items.valor })),
            categories: detalleNoPlane[0].items.periodo,
        };
    }, [detalleNoPlane]);

    // ─── Chart options ────────────────────────────────────────────────────────────
    const barBase = useMemo(() => ({
        chart: {
            type: 'bar', stacked: true,
            toolbar: { show: false }, zoom: { enabled: false },
            fontFamily: 'WorkSans',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                animateGradually: { enabled: true, delay: 80 },
                dynamicAnimation: { enabled: true, speed: 400 },
            },
        },
        plotOptions: { bar: { horizontal: false, columnWidth: '50%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '13px', fontFamily: 'WorkSans' },
        fill: { opacity: 1 },
        grid: { borderColor: '#e9ecf0' },
        yaxis: { labels: { style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' } } },
        noData: {
            text: 'Sin datos para esta selección',
            align: 'center', verticalAlign: 'middle',
            style: { fontSize: '15px', fontFamily: 'WorkSans', color: '#5f606f' },
        },
    }), []);

    const xLabels = { labels: { style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' } } };

    const opt1 = useMemo(() => ({
        ...barBase,
        colors: ['#6495ED'],
        xaxis: { ...xLabels, categories: chart1.categories },
    }), [barBase, chart1.categories]); // eslint-disable-line react-hooks/exhaustive-deps

    const opt2 = useMemo(() => ({
        ...barBase,
        colors: ['#fe424d', '#6495ED'],
        xaxis: { ...xLabels, categories: chart2.categories },
    }), [barBase, chart2.categories]); // eslint-disable-line react-hooks/exhaustive-deps

    const opt3 = useMemo(() => ({
        ...barBase,
        colors: MULTI_COLORS,
        xaxis: { ...xLabels, categories: chart3.categories },
    }), [barBase, chart3.categories]); // eslint-disable-line react-hooks/exhaustive-deps

    const opt4 = useMemo(() => ({
        ...barBase,
        colors: MULTI_COLORS,
        xaxis: { ...xLabels, categories: chart4.categories },
    }), [barBase, chart4.categories]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Empty / loading states ───────────────────────────────────────────────
    if (isLoadingLlamadas) {
        return <div className="ro-state">Cargando reporte...</div>;
    }

    if (!tPerdidosFetched) {
        return (
            <div className="ro-state">
                Seleccione línea y/o celda y presione «Ver Reporte»
            </div>
        );
    }

    return (
        <div className="tiempos-container">

            {/* KPI row */}
            {kpis.total > 0 && (
                <div className="pareto-kpi-row">
                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--blue">
                            <Clock size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">{kpis.total.toLocaleString()} min</span>
                            <span className="pareto-kpi-card-label">
                                total tiempos perdidos{nombreFiltro ? ` · ${nombreFiltro}` : ''}
                            </span>
                        </div>
                    </div>
                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--red">
                            <TrendingDown size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">{kpis.pctNoPlaneados} %</span>
                            <span className="pareto-kpi-card-label">no planeados</span>
                        </div>
                    </div>
                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--orange">
                            <Calendar size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">{kpis.pctPlaneados} %</span>
                            <span className="pareto-kpi-card-label">planeados</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Row 1: global + planeados vs no planeados */}
            <div className="tiempos-charts-grid">
                <div className="tiempos-chart-card">
                    <p className="tiempos-chart-title">
                        Tiempos perdidos{nombreFiltro ? ` · ${nombreFiltro}` : ''}
                    </p>
                    <Chart options={opt1} series={chart1.series} type="bar" width="100%" height={260} />
                </div>
                <div className="tiempos-chart-card">
                    <p className="tiempos-chart-title">Planeados vs No Planeados</p>
                    <Chart options={opt2} series={chart2.series} type="bar" width="100%" height={260} />
                </div>
            </div>

            {/* Detail charts */}
            <div className="tiempos-charts-grid">
                <div className="tiempos-chart-card">
                    <p className="tiempos-chart-title">Detalle — Paros Planeados</p>
                    <Chart options={opt3} series={chart3.series} type="bar" width="100%" height={300} />
                </div>
                <div className="tiempos-chart-card">
                    <p className="tiempos-chart-title">Detalle — Paros No Planeados</p>
                    <Chart options={opt4} series={chart4.series} type="bar" width="100%" height={300} />
                </div>
            </div>

        </div>
    );
}
